import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useOperator } from './contexts/OperatorContext';
import { useOrganizationByTenantQuery } from '../generated/graphql';
import { QUALITY_USERLESS_CLIENT } from './apollo/constants';

import useAuth from 'Modules/firebase/use-auth';
import Loader from './Components/Loader';
import LoadingPage from './Pages/LoadingPage';
import LoginPage from './Pages/LoginPage';
import AccessDeniedPage from '@/operator/pages/access/AccessDenied';
import { Outlet, useNavigate, useParams } from 'react-router';
import { useSubdomain } from "hooks/useSubdomain";
import { useUserOrgs } from "@/operator/hooks/useUserOrgs";
import ConfirmationModal from "Components/Common/ConfirmationModal";
import { getOrgPathName } from "@/operator/utils/env/urlStrings";
import { Typography } from "@mui/material";

const SubdomainEnforcer: React.FC = (): ReactElement => {
    const { isAuthenticated, loading: loadingAuth, error, logout } = useAuth();
    const { organizationId, setOrganizationId } = useOperator();
    const { isAppRoot, isSubdomainRoute, subdomain } = useSubdomain();
    const { userOrganizations, loading: loadingUserOrgs } = useUserOrgs(false);
    const { orgId: orgSlug } = useParams<{ orgId: string }>();
    
    const navigate = useNavigate();

    const { data, loading: loadingOrg } = useOrganizationByTenantQuery({
        context: { clientName: QUALITY_USERLESS_CLIENT },
        variables: { tenant: subdomain },
        skip: isAppRoot,
    });

    const [showMismatchModal, setShowMismatchModal] = useState(false);
    
    const activeOrg = useMemo(
        () =>
            userOrganizations?.find((org) => org.orgId === organizationId) ?? null,
        [userOrganizations, organizationId, orgSlug]
    );

    useEffect(() => {
        if(loadingUserOrgs) return;
 
        if (
            isAuthenticated &&
            activeOrg &&
            orgSlug &&
            activeOrg.slug !== orgSlug &&
            isSubdomainRoute
        ) {
            setShowMismatchModal(true);
        }
    }, [isAuthenticated, activeOrg?.slug, orgSlug, organizationId, userOrganizations, loadingUserOrgs]);

    useEffect(() => {
        if (isSubdomainRoute && !loadingOrg && !organizationId) {
            setOrganizationId(data?.organizationByTenant?.id);
        }
    }, [loadingOrg, isSubdomainRoute]);

    const isOnInviteRoute = window.location.pathname.includes('/invite');

    const handleLogoutAndSwitch = async () => {
        const pickedOrg = userOrganizations?.find((org) => org.slug === orgSlug);
        setShowMismatchModal(false);
        await logout();
        setOrganizationId(pickedOrg?.orgId || '');
    };

    const handleStay = () => {
        setShowMismatchModal(false);
        const activeSlug = activeOrg?.slug;
        if (activeSlug !== 'app' && activeSlug !== orgSlug) {
            const activeOrgPath = getOrgPathName(activeSlug);
            navigate(activeOrgPath, { replace: true });
        }
    };

    if (loadingOrg) {
        return (
            <LoadingPage container>
                <Loader message="Loading organization data" />
            </LoadingPage>
        );
    }

    if (error) {
        return <AccessDeniedPage />;
    }

    if (organizationId && isSubdomainRoute && !loadingAuth && !isOnInviteRoute) {
        if (
            !isAuthenticated
            // || user?.org_id !== organizationId) {
        ) {
            return (
                <LoginPage
                    params={{
                        authorizationParams: {
                            organization:
                                organizationId !== ''
                                    ? organizationId
                                    : undefined,
                        },
                    }}
                />
            );
        }
    }

    return (
        <>
            <Outlet />
            {showMismatchModal && (
                <ConfirmationModal
                    open={true}
                    onClose={handleStay}
                    title="Switch Organization?"
                    content={
                        <Typography>
                            You are trying to access a different organization than your current session. <br/>Would you like to log out and switch to <strong>{orgSlug}</strong>?
                        </Typography>
                    }
                    footer={{
                        primaryButton: {
                            label: 'Switch',
                            onClicK: handleLogoutAndSwitch
                        },
                        secondaryButton: {
                            label: 'Stay',
                            onClicK: handleStay,
                        }
                    }}
                />
            )}
        </>
    );
};

export default SubdomainEnforcer;
